import React from "react";
import { useTranslation } from "react-i18next";
import { Sprout, Compass, Building2, ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  SEEKER_TYPE,
  EXPERT_TYPE,
  ORGANIZATION_TYPE,
  type UserType,
} from "../../lib/userType";

interface UserTypeCardsProps {
  onSelect: (type: UserType) => void;
  selected?: UserType | null;
  className?: string;
}

/**
 * The three signup gates: grow (seeker) / coach or expert (Studio) /
 * organization. Presentational — used by Register and by the in-app
 * signup fork on the register page.
 */
const UserTypeCards: React.FC<UserTypeCardsProps> = ({
  onSelect,
  selected,
  className,
}) => {
  const { t } = useTranslation();

  const cards: Array<{
    type: UserType;
    icon: typeof Sprout;
    title: string;
    description: string;
    accent: string;
  }> = [
    {
      type: SEEKER_TYPE,
      icon: Sprout,
      title: t("ut_seeker_title", "I'm here to grow"),
      description: t(
        "ut_seeker_desc",
        "Guided journeys, reflection, and habits — become who you want to be."
      ),
      accent: "text-emerald-600 bg-emerald-50",
    },
    {
      type: EXPERT_TYPE,
      icon: Compass,
      title: t("ut_expert_title", "I'm a coach or expert"),
      description: t(
        "ut_expert_desc",
        "Build transformation journeys your clients actually finish — with the ClaryNext Studio."
      ),
      accent: "text-primary bg-primary/10",
    },
    {
      type: ORGANIZATION_TYPE,
      icon: Building2,
      title: t("ut_org_title", "For my organization"),
      description: t(
        "ut_org_desc",
        "Growth journeys for your customers or employees — let's talk."
      ),
      accent: "text-amber-600 bg-amber-50",
    },
  ];

  return (
    <div className={cn("grid gap-3", className)}>
      {cards.map(({ type, icon: Icon, title, description, accent }) => (
        <button
          key={type}
          type="button"
          onClick={() => onSelect(type)}
          className={cn(
            "group flex items-center gap-4 rounded-xl border p-4 text-start transition-all",
            selected === type
              ? "border-primary bg-primary/5 shadow-sm"
              : "border-border bg-background hover:border-primary/50 hover:bg-accent/40"
          )}
        >
          <span
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg",
              accent
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-foreground">
              {title}
            </span>
            <span className="block text-xs text-muted-foreground leading-relaxed mt-0.5">
              {description}
            </span>
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
        </button>
      ))}
    </div>
  );
};

export default UserTypeCards;
